import { css, cx } from '@emotion/css';
import { isString } from 'lodash';
import { useEffect, useState } from 'react';

import {
  TILE_DEFAULT_FONT_SIZE_VARIANT,
  TileFontSizeVariant,
  TileGroup,
  TileGroupItem,
  TileVariant,
} from '@/components/diagrams/components/Tile';
import { TileNode } from '@/components/diagrams/FlowChart';
import { useCursorStyles } from '@/components/diagrams/FlowChart/hooks/useCursorStyles';
import { COLORS } from '@/styles/tokens/colors';
import { AfterDeath } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { formatCurrency } from '@/utils/formatting/currency';
import { sortDecimal } from '@/utils/sortUtils';

import { useEstateWaterfallContext } from '../contexts/estateWaterfall.context';
import {
  getIsDraftNode,
  isHypotheticalWaterfall,
} from '../EstateWaterfall.utils';
import { EstateWaterfall_NodeFragment } from '../graphql/EstateWaterfall.generated';
import { CreateNewGroupModal } from './CreateNewGroupModal';
import { buildTileFromNode, getNodeId } from './utils';

const tileGroupItemRemoveHoverStyle = css({
  /**
   * For some reason styles are loading in out of order with the classes / useStyles pattern
   * so the important is needed.
   * Unclear if this was a library upgrade or something else.
   * @todo: https://linear.app/luminary/issue/T1-1142/classes-pattern-not-applying-styles-in-correct-order
   */
  '&:hover': {
    background: 'none !important',
  },
});

const forceHoverStyles = css({
  backgroundColor: COLORS.FUNCTIONAL.HOVER,
});

interface GroupNodeItemProps {
  groupNodeId: string;
  node: EstateWaterfall_NodeFragment;
  variant: TileVariant;
  fontSizeVariant: TileFontSizeVariant;
}

function GroupNodeItem({
  groupNodeId,
  node,
  variant,
  fontSizeVariant,
}: GroupNodeItemProps) {
  const { dispatch, state } = useEstateWaterfallContext();
  const {
    cursorClassName: parentCursorClassName,
    isInteractive,
    styles,
  } = useCursorStyles(groupNodeId);

  // Items are only clickable in the normal view mode — not while editing and
  // not while embedded in a presentation, where the diagram should be inert.
  const isClickable = !isInteractive && !state.presentationMode;

  const isDraft = getIsDraftNode(node.node);

  const tile = buildTileFromNode({
    visualizationConfig: state.waterfall.visualizationConfig,
    nodeFragment: node,
    isHypotheticalWaterfall: isHypotheticalWaterfall(state.waterfall),
  });

  useEffect(() => {
    if (tile && !isString(tile.data.lineOne)) {
      const error = new Error('lineOne must be a string in GroupNodeItems');
      diagnostics.error(error.message, error, { groupNodeId, nodeId: tile.id });
    }
  }, [tile, groupNodeId]);

  if (!tile) return null;

  const { id, data } = tile;
  const lineOne = isString(data.lineOne) ? data.lineOne : '';

  const summaryNode = state.derivedData.summaryNode;

  // Format the individual node value for display
  const formattedValue = state.waterfall.visualizationConfig?.hideEntityValues
    ? undefined
    : formatCurrency(node.value, {
        notation: 'compact',
        currencySign: 'accounting',
      });

  return (
    <TileGroupItem
      key={id}
      description={isDraft ? `[Draft] ${lineOne}` : lineOne}
      value={formattedValue}
      tileVariant={variant}
      fontSizeVariant={fontSizeVariant}
      classes={{
        root: cx(
          parentCursorClassName,
          // Parent is a tile group, which will never be clickable.
          // We want to inherit the parent styles always, but apply the clickable styles
          // only when the item is clickable.
          isClickable && styles.clickable,
          // No hover styles when a click can't happen
          !isClickable && tileGroupItemRemoveHoverStyle,
          // Force hover styles when this item is opened
          summaryNode?.node?.id === id && forceHoverStyles
        ),
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        // Only open the summary panel if the item is clickable
        isClickable && dispatch({ type: 'OPEN_SUMMARY_PANEL', nodeId: id });
      }}
    />
  );
}

interface GroupNodeItemsProps {
  groupNodeId: string;
  nodes: EstateWaterfall_NodeFragment[];
  shouldTriggerCreateGroupModal: boolean;
  namespace: AfterDeath;
  variant: TileVariant;
}

export function GroupNodeItems({
  groupNodeId,
  nodes,
  shouldTriggerCreateGroupModal,
  namespace,
  variant,
}: GroupNodeItemsProps) {
  const { state } = useEstateWaterfallContext();
  const existingNode = state.derivedData.nodesById[groupNodeId];
  const existingNodeData = existingNode?.data as TileNode['data'] | undefined;
  const hideList =
    existingNodeData?.estateWaterfallConfig?.hideGroupContents ||
    state.waterfall.visualizationConfig?.hideEntitiesListInGroups;

  const fontSizeVariant =
    (existingNode?.data as { fontSizeVariant?: TileFontSizeVariant })
      ?.fontSizeVariant ?? TILE_DEFAULT_FONT_SIZE_VARIANT;
  const showList = !hideList;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(
    shouldTriggerCreateGroupModal
  );

  // Sync local state when the prop changes to true (for subsequent group creations)
  useEffect(() => {
    if (shouldTriggerCreateGroupModal) {
      setIsCreateModalOpen(true);
    }
  }, [shouldTriggerCreateGroupModal]);

  return (
    <>
      {isCreateModalOpen && state.isInEditMode && (
        <CreateNewGroupModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
          }}
          nodeIds={nodes.map((node) => node.id)}
          namespace={namespace}
        />
      )}
      {showList && (
        <TileGroup
          tileVariant={variant}
          fontSizeVariant={fontSizeVariant}
          hideItemCount
        >
          {[...nodes]
            .sort((a, b) => sortDecimal(b.value, a.value))
            .map((node) => {
              const { id, afterDeath } = node;
              const key = getNodeId({ id, afterDeath });
              return (
                <GroupNodeItem
                  key={key}
                  node={node}
                  groupNodeId={groupNodeId}
                  variant={variant}
                  fontSizeVariant={fontSizeVariant}
                />
              );
            })}
        </TileGroup>
      )}
    </>
  );
}
